import { yupResolver } from "@hookform/resolvers/yup"
import { Stack, Typography } from "@mui/material"
import { useEffect, useState } from "react"
import { type Resolver } from "react-hook-form"
import * as yup from "yup"

import DialogProvider from "@components/dialog_provider"
import { useCustomerPageContext } from "@contexts/customer"
import { deleteCustomerPaymentCard } from "@domain/auth"
import useControlledForm from "@hooks/use_controlled_form"
import { PaymentMethodSaveInformationData } from "@pages/customer/infos/tabs/plan_billing/sub_components/payment_information"
import { showErrorToast } from "@utils/toast"

export type DeletePaymentCardForm = {
  paymentCardId: string
}

export type DeletePaymentCardProps = {
  isDeletePaymentCardOpen: boolean
  toggleIsDeletePaymentCardOpen: () => void
  customerId?: string
  paymentCardId?: string
  setPaymentMethod: (
    value: PaymentMethodSaveInformationData | undefined
  ) => void
}

export default function DeletePaymentCard({
  isDeletePaymentCardOpen,
  toggleIsDeletePaymentCardOpen,
  customerId,
  paymentCardId,
  setPaymentMethod
}: DeletePaymentCardProps) {
  const { translate } = useCustomerPageContext()
  const [loading, setLoading] = useState(false)

  const schema = yup
    .object({
      paymentCardId: yup.string().required()
    })
    .required()

  const {
    handleSubmit,
    setValue,
    reset,
    formState: { isValid }
  } = useControlledForm<DeletePaymentCardForm>({
    resolver: yupResolver(schema) as Resolver<DeletePaymentCardForm>,
    mode: "all",
    defaultValues: {
      paymentCardId: paymentCardId
    }
  })

  function handleClose() {
    if (loading) {
      return
    }
    toggleIsDeletePaymentCardOpen?.()
    reset()
  }

  async function onSubmit() {
    if (!customerId || !paymentCardId) return
    setLoading(true)
    const { isSuccess, errorMessage } = await deleteCustomerPaymentCard({
      id: paymentCardId,
      customerId: customerId
    })
    if (!isSuccess) {
      setLoading(false)
      showErrorToast(String(errorMessage))
      return { options: [], hasMore: false }
    }
    setLoading(false)
    handleClose()
    setPaymentMethod?.(undefined as unknown as PaymentMethodSaveInformationData)
  }

  useEffect(() => {
    if (paymentCardId) {
      setValue("paymentCardId", paymentCardId, { shouldValidate: true })
    }
  }, [isDeletePaymentCardOpen])

  return (
    <DialogProvider
      title={translate("delete_payment_card")}
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: handleClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("delete"),
        color: "error",
        onClick: handleSubmit(onSubmit),
        loading: loading,
        disabled: !isValid
      }}
      isOpen={!!isDeletePaymentCardOpen}
      setClose={handleClose}
      size="large"
    >
      <Stack flex={1} gap={1} justifyContent={"center"} alignItems={"center"}>
        <Typography>{translate("delete_payment_card_description")}</Typography>
      </Stack>
    </DialogProvider>
  )
}
